import { defineStore } from "pinia";
import { assessmentApi } from "@/services/assessmentApi";

function isoDate(d = new Date()) {
  return new Date(d).toISOString().slice(0, 10);
}

function daysBetween(aMs, bMs) {
  return Math.floor((aMs - bMs) / (1000 * 60 * 60 * 24));
}

function startOfWeekISO(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay(); // 0 Sun..6 Sat
  const diff = (day === 0 ? -6 : 1 - day);
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

const DIAGNOSTIC_INTERVAL_DAYS = 10;

/**
 * AssessmentStore
 * --------------
 * Stores diagnostic attempts per child.
 *
 * Structure:
 * diagnostics[childId] = [{ timestamp, report }]
 */

export const useAssessmentStore = defineStore("assessmentStore", {
  state: () => ({
    diagnostics: {},
    // lastDiagnostic[childId] = timestamp
    lastDiagnostic: {},
    tests: {},
    // Track loading state per child
    _meta: {
      loadedChildren: {},  // childId -> true when loaded from API
      loading: false
    }
  }),

  getters: {
    history: (state) => (childId) => {
      const list = state.diagnostics?.[childId];
      return Array.isArray(list) ? list : [];
    },

    latest: (state) => (childId) => {
      const list = state.diagnostics?.[childId];
      if (!Array.isArray(list) || list.length === 0) return null;
      return list[list.length - 1];
    },

    /**
     * Convenience getter used by StudentDashboard:
     * returns the latest diagnostic "results" object (sgId -> score/level).
     */
    getDiagnostic: (state) => (childId) => {
      const list = state.diagnostics?.[childId];
      if (!Array.isArray(list) || list.length === 0) return null;
      const latest = list[list.length - 1];
      if (latest?.report?.results) return latest.report.results;
      if (latest?.report && typeof latest.report === "object") return latest.report;
      return null;
    },

    lastDiagnosticDate: (state) => (childId) => {
      const list = state.diagnostics?.[childId];
      if (!Array.isArray(list) || list.length === 0) return null;
      return list[list.length - 1]?.timestamp || null;
    },

    // Optional helper for WeeklyTargetEngine
    latestAbilityBandLabel: (state) => (childId) => {
      const last = (state.diagnostics?.[childId] || []).slice(-1)[0];
      const label = last?.profile?.abilityBand?.label || last?.profile?.abilityBand?.level || null;
      return label;
    },

    testsHistory: (state) => (childId) => {
      return state.tests?.[childId] || [];
    },

    hasTestThisWeek: (state) => (childId) => {
      const wk = startOfWeekISO(new Date());
      const list = state.tests?.[childId] || [];
      return list.some(t => t.weekStart === wk);
    },

    hasTestToday: (state) => (childId, dateISO = isoDate()) => {
      const list = state.tests?.[childId] || [];
      return list.some(t => t.dateISO === dateISO);
    },
    hasDiagnosticToday: (state) => (childId, dateISO = isoDate()) => {
      const list = state.diagnostics?.[childId] || [];
      return list.some(t => t.dateISO === dateISO);
    }
  },

  actions: {
    ensureChild(childId) {
      if (!this.diagnostics[childId]) this.diagnostics[childId] = [];
      if (!this.tests[childId]) this.tests[childId] = [];
    },

    /**
     * Load assessments from API for a child.
     * Only loads once per session unless force=true.
     */
    async loadAssessments(childId, force = false) {
      if (!childId) return;

      // Skip if already loaded (unless forced)
      if (!force && this._meta.loadedChildren[childId]) {
        return;
      }

      this._meta.loading = true;
      try {
        const data = await assessmentApi.loadAll(childId);

        // Replace local state with API data
        this.diagnostics[childId] = data.diagnostics || [];
        this.tests[childId] = data.tests || [];

        // Update lastDiagnostic from loaded data
        const diagList = this.diagnostics[childId];
        if (diagList.length > 0) {
          this.lastDiagnostic[childId] = diagList[diagList.length - 1]?.timestamp || null;
        }

        this._meta.loadedChildren[childId] = true;
      } catch (err) {
        console.error('Failed to load assessments:', err);
      } finally {
        this._meta.loading = false;
      }
    },

    async saveDiagnostic(childId, report, profile = null) {
      if (!childId) return;
      this.ensureChild(childId);

      // Optimistic local update
      const ts = Date.now();
      const entry = {
        type: "diagnostic",
        timestamp: ts,
        dateISO: isoDate(ts),
        weekStart: startOfWeekISO(new Date(ts)),
        report,
        profile
      };
      this.diagnostics[childId].push(entry);
      this.lastDiagnostic[childId] = ts;

      // Persist to API (fire and forget with error logging)
      try {
        await assessmentApi.saveDiagnostic(childId, report, profile);
      } catch (err) {
        console.error('Failed to save diagnostic to API:', err);
      }
    },

    async saveTestAttempt(childId, { kind = "quiz", score = null, maxScore = null, meta = {} } = {}) {
      if (!childId) return;
      this.ensureChild(childId);

      // Optimistic local update
      const ts = Date.now();
      const entry = {
        type: "test",
        kind, // "quiz" | "mock" | "timed"
        timestamp: ts,
        dateISO: isoDate(ts),
        weekStart: startOfWeekISO(new Date(ts)),
        score,
        maxScore,
        meta
      };
      this.tests[childId].push(entry);

      // Persist to API (fire and forget with error logging)
      try {
        await assessmentApi.saveTest(childId, { kind, score, maxScore, meta });
      } catch (err) {
        console.error('Failed to save test to API:', err);
      }
    },

    overwriteLatest(childId, newReport) {
      const list = this.diagnostics?.[childId];
      if (!Array.isArray(list) || list.length === 0) return;
      list[list.length - 1].report = newReport;
    },

    isDiagnosticDue(childId) {
      return this.diagnosticStatus(childId).state !== "not-due";
    },

    diagnosticStatus(childId) {
      const last = this.lastDiagnosticDate(childId);
      if (!last) return {
        state: "due",
        label: "Diagnostic due",
        days: null
      };

      const daysSince = daysBetween(Date.now(), last);
      const daysRemaining = DIAGNOSTIC_INTERVAL_DAYS - daysSince;

      if (daysRemaining > 0) {
        return {
          state: "not-due",
          label: `Due in ${daysRemaining} day${daysRemaining === 1 ? "" : "s"}`,
          days: daysRemaining
        };
      }

      return {
        state: "overdue",
        label: `Overdue by ${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) === 1 ? "" : "s"}`,
        days: Math.abs(daysRemaining)
      };
    }

  }
});
